/** @jsxImportSource @emotion/react */

import { jsonToCsv } from '@quickbit/backoffice-core-frontend';
import { UtilityButton } from '@quickbit/qb-design-system';
import { useAsyncFn } from 'react-use';
import { getAccountingOrders } from '../api';
import { Order, AccountingOrderFilter, SortOptions } from '../types';

const PAGE_SIZE = 50;

interface Props {
  filters: AccountingOrderFilter;
  sortOptions?: SortOptions;
  disabled?: boolean;
  csvFileName: string;
}

type Columns =
  | 'Order ID'
  | 'Reference ID'
  | 'Blockchain transaction ID'
  | 'Amount'
  | 'Currency'
  | 'Created At'
  | 'Completed At'
  | 'Order Type';

type Row = Record<Columns, string | number | null>;

const orderToRow = (order: Order): Row => {
  return {
    'Order ID': order.uuid,
    'Reference ID': order.referenceId,
    'Blockchain transaction ID':
      (order.extraData.blockchainTransactionId as string | undefined) ?? '',
    Amount: order.amount,
    Currency: order.currency,
    'Created At': order.createdAt.toLocaleString(),
    'Completed At': order.completedAt?.toLocaleString() ?? '',
    'Order Type': order.useProcessingFlow
      ? 'Processing'
      : order.useOnrampFlow
      ? 'Onramp'
      : 'Pay',
  };
};

export const ExportCsv = ({
  filters,
  sortOptions,
  disabled,
  csvFileName,
}: Props) => {
  const [{ loading: downloadCsvLoading }, downloadCsv] =
    useAsyncFn(async () => {
      const page = 1;
      const firstOrders = await getAccountingOrders({
        pagination: { size: PAGE_SIZE, page },
        filters,
        sortOptions,
      });
      const lastPage = Math.ceil(
        firstOrders.aggregatedData.orderCount / PAGE_SIZE
      );
      const data: Row[] = firstOrders.orders.map((order) => orderToRow(order));

      let i = page + 1;
      let errorCount = 0;
      while (i <= lastPage) {
        try {
          const orders = await getAccountingOrders({
            pagination: { size: PAGE_SIZE, page: i },
            filters,
            sortOptions,
          });
          data.push(...orders.orders.map((order) => orderToRow(order)));
          i++;
          errorCount = 0;
        } catch (e) {
          errorCount++;
          if (errorCount >= 5) {
            throw new Error('Failed to fetch order data');
          }
          console.error('Failed to fetch order data, trying again', e);
        }
      }

      jsonToCsv({
        data: data,
        filename: csvFileName,
        columns: [
          'Order ID',
          'Reference ID',
          'Blockchain transaction ID',
          'Amount',
          'Currency',
          'Created At',
          'Completed At',
          'Order Type',
        ] as Columns[],
      });
      return data;
    }, [filters, sortOptions]);

  return (
    <UtilityButton
      text="Extract as csv"
      onClick={downloadCsv}
      loading={downloadCsvLoading}
      disabled={disabled || downloadCsvLoading}
    />
  );
};
